import React, { useEffect, useRef, useState } from "react";
import { astryx, extractErrorMessage, type IScriptTemplate } from "@shadowcue/ui";
import {
  generateScriptTemplate,
  generateMode,
  getModalValuesFromMode,
} from "./utils";
import { getGenerationSteps, useGenerationProgress } from "./progress";
import GenerationTrace from "./generation_trace";
import type { IUser } from "../../../../types/iuser";

// Long enough for the outline to settle and "ready" to register before the
// modal closes.
const FINISHED_HOLD_MS = 800;

// Re-export this so importers of the modal get everything in one place
export { generateMode }

interface GenerateScriptModalProps {
  isOpen: boolean;
  mode: generateMode;
  /** Dismisses without doing anything — the close button, and Cancel. */
  onClose: () => void;
  /**
   * "Start from scratch" is a choice, not a dismissal: on the index it means
   * "take me to the blank form". Without it that button just closes.
   */
  onStartFromScratch?: () => void;
  /** The current form values, sent along when refining. Omitted for a fresh start. */
  scriptTemplate: IScriptTemplate;
  /** Receives the generated template to fill the form with. */
  onGenerated: (template: IScriptTemplate) => void;
  /** Supplies the Bearer token for the generation request. */
  currentUser: IUser;
}


const GenerateScriptModal = ({
  isOpen,
  mode,
  onClose,
  onStartFromScratch,
  scriptTemplate,
  onGenerated,
  currentUser,
}: GenerateScriptModalProps): React.ReactElement => {
  // Because of all of the various places text changes based on the mode, we collect
  // the values together in one place for easier reference.

  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const currentModalValues = getModalValuesFromMode(mode);
  const [error, setError] = useState<string | null>(null);
  const progress = useGenerationProgress(getGenerationSteps(mode));
  // A generation can run for up to two minutes. Closing the dialog cancels
  // it, and the browser's Back button can unmount this page mid-flight. Abort
  // the request in both cases, and never act on a result nobody is waiting
  // for (on the index that result would navigate the user away from wherever
  // they went).
  const isMountedRef = useRef(true);
  const controllerRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      isMountedRef.current = false;
      controllerRef.current?.abort();
    },
    [],
  );

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) return;
    if (isGenerating) {
      // The X cancels. The prompt stays in state, so reopening picks up
      // where the user left off.
      controllerRef.current?.abort();
      progress.reset();
    }
    // A stale server message shouldn't greet the next attempt.
    setError(null);
    onClose();
  };

  // The dialog itself closes on Escape. While a script is generating, only
  // the header's X should cancel, so a stray keypress can't throw away a
  // minute of work.
  const handleDialogOpenChange = (nextIsOpen: boolean) => {
    if (isGenerating) return;
    handleOpenChange(nextIsOpen);
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;

    const controller = new AbortController();
    controllerRef.current = controller;
    setIsGenerating(true);
    setError(null);
    progress.start();
    try {
      const template = await generateScriptTemplate({
        prompt,
        scriptTemplate: scriptTemplate,
        authToken: currentUser.authToken,
        signal: controller.signal,
      });
      if (!isMountedRef.current || controller.signal.aborted) return;
      progress.finish();
      await new Promise((resolve) => setTimeout(resolve, FINISHED_HOLD_MS));
      // Closed during the hold: that's still a cancel.
      if (!isMountedRef.current || controller.signal.aborted) return;
      onGenerated(template);
      // Cleared because the next prompt refines the new result rather than
      // repeating this one.
      setPrompt("");
      onClose();
      progress.reset();
    } catch (caught) {
      // A cancel isn't an error worth showing.
      if (!isMountedRef.current || controller.signal.aborted) return;
      // Back to the prompt, still filled in, so the user can adjust and retry.
      progress.reset();
      setError(extractErrorMessage(caught));
    } finally {
      if (isMountedRef.current) setIsGenerating(false);
    }
  };

  return (
    <astryx.Dialog
      isOpen={isOpen}
      onOpenChange={handleDialogOpenChange}
      purpose="form"
      width={560}
    >
      <astryx.Layout
        header={
          <astryx.DialogHeader
            title={progress.isActive ? currentModalValues.progressTitle : currentModalValues.title}
            subtitle={progress.isActive ? undefined : currentModalValues.subtitle}
            onOpenChange={handleOpenChange}
          />
        }
        content={
          <astryx.LayoutContent>
            <astryx.VStack gap={4}>
              {progress.isActive ? (
                <GenerationTrace
                  stepIndex={progress.stepIndex}
                  statusMessage={progress.statusMessage}
                  isDone={progress.isDone}
                  isRevising={mode === generateMode.REGENERATE}
                />
              ) : (
                <astryx.TextArea
                  label={currentModalValues.field}
                  value={prompt}
                  onChange={setPrompt}
                  placeholder={
                    mode === "regenerate"
                      ? "e.g. Add a section on system design, and make the technical questions harder."
                      : "e.g. We're hiring a senior backend engineer for our payments team. Rails and Postgres, lots of async work. I want to understand how they handle ambiguity and whether they've owned something end to end."
                  }
                  rows={6}
                  width="100%"
                  isDisabled={isGenerating}
                  hasAutoFocus
                />
              )}

              {/* Detached: this floats below the field with a gap, so it must
                  not use the default attached variant (square top corners,
                  meant to tuck under an input). */}
              {error && (
                <astryx.FieldStatus type="error" message={error} variant="detached" />
              )}

              {/* Mounted with the dialog, before any generation starts: a
                  live region that appears already holding text isn't
                  announced, so the first step would be skipped. */}
              <astryx.VisuallyHidden>
                <span aria-live="polite">{progress.isActive ? progress.statusMessage : ""}</span>
              </astryx.VisuallyHidden>
            </astryx.VStack>
          </astryx.LayoutContent>
        }
        // Nothing to act on while generating; the header's X cancels.
        footer={progress.isActive ? undefined : (
          <astryx.LayoutFooter hasDivider>
            <astryx.HStack gap={2} justify="end">
              <astryx.Button
                label={currentModalValues.dismiss}
                onClick={
                  mode === "initial" && onStartFromScratch
                    ? onStartFromScratch
                    : () => handleOpenChange(false)
                }
                isDisabled={isGenerating}
              />
              <astryx.Button
                label={currentModalValues.submit}
                variant="primary"
                onClick={handleGenerate}
                isLoading={isGenerating}
                isDisabled={!prompt.trim()}
              />
            </astryx.HStack>
          </astryx.LayoutFooter>
        )}
      />
    </astryx.Dialog>
  );
};

export default GenerateScriptModal;
